import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type {
  TemplateFormData,
  TestTemplate,
  ValidationResult,
} from './hooks/use-test-templates'
import {
  type LanguageCodeData,
  MultiLanguageCodeEditor,
  type SupportedLanguage,
} from './multi-language-code-editor'

interface TemplateEditorModalProps {
  isOpen: boolean
  template?: TestTemplate | null // null = create mode, template = edit mode
  onClose: () => void
  onSave: (data: TemplateFormData) => Promise<TestTemplate>
  onValidate: (
    id: string,
    language: string,
    options?: {
      definitionCode?: string
      helperCode?: string
      testCode?: string
      testExpectedOutput?: string
    },
  ) => Promise<ValidationResult>
  isSubmitting?: boolean
}

export function TemplateEditorModal({
  isOpen,
  template,
  onClose,
  onSave,
  onValidate,
  isSubmitting = false,
}: TemplateEditorModalProps) {
  const isEditMode = !!template

  // Form state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [codeData, setCodeData] = useState<Record<string, LanguageCodeData>>({
    python: { definitionCode: '' },
    javascript: { definitionCode: '' },
  })
  const [validating, setValidating] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  // Reset form when modal opens/closes or template changes
  useEffect(() => {
    if (!isOpen) return

    if (template) {
      setName(template.name)
      setDescription(template.description || '')
      // Convert template code array to record
      const newCodeData: Record<string, LanguageCodeData> = {
        python: { definitionCode: '' },
        javascript: { definitionCode: '' },
      }
      for (const codeItem of template.code) {
        newCodeData[codeItem.language] = {
          definitionCode: codeItem.definitionCode,
          helperCode: codeItem.helperCode,
          testCode: codeItem.testCode,
          testExpectedOutput: codeItem.testExpectedOutput,
          lastValidatedAt: codeItem.lastValidatedAt,
        }
      }
      setCodeData(newCodeData)
    } else {
      // Reset for create mode
      setName('')
      setDescription('')
      setCodeData({
        python: { definitionCode: '' },
        javascript: { definitionCode: '' },
      })
    }
    setValidationError(null)
  }, [isOpen, template])

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  const handleValidate = async (
    language: string,
  ): Promise<{ success: boolean; error?: string }> => {
    if (!template?.id) {
      // For new templates, we need to save first before validating
      setValidationError('Please save the template first before validating')
      return { success: false, error: 'Save template first' }
    }

    setValidating(true)
    setValidationError(null)

    try {
      const langData = codeData[language]
      const result = await onValidate(template.id, language, {
        definitionCode: langData.definitionCode,
        helperCode: langData.helperCode,
        testCode: langData.testCode,
        testExpectedOutput: langData.testExpectedOutput,
      })

      if (result.success) {
        // Update lastValidatedAt in local state
        setCodeData((prev) => ({
          ...prev,
          [language]: {
            ...prev[language],
            lastValidatedAt: new Date().toISOString(),
          },
        }))
        toast.success('Validation passed')
      } else {
        setValidationError(result.error || 'Validation failed')
        toast.error(result.error || 'Validation failed')
      }

      return result
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Validation failed'
      setValidationError(errorMsg)
      toast.error(errorMsg)
      return { success: false, error: errorMsg }
    } finally {
      setValidating(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      toast.error('Name is required')
      return
    }

    // Check if at least one language has code
    const hasCode = Object.values(codeData).some((d) => d.definitionCode.trim())
    if (!hasCode) {
      toast.error('Please add code for at least one language')
      return
    }

    // Convert codeData to array format for API
    const codeArray = Object.entries(codeData)
      .filter(([, data]) => data.definitionCode.trim())
      .map(([language, data]) => ({
        language,
        definitionCode: data.definitionCode,
        helperCode: data.helperCode,
        testCode: data.testCode || '',
        testExpectedOutput: data.testExpectedOutput || '',
      }))

    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || undefined,
        code: codeArray,
      })
      onClose()
    } catch {
      // Error is handled by parent
    }
  }

  if (!isOpen) return null

  const languages: SupportedLanguage[] = ['python', 'javascript']

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            {isEditMode ? 'Edit Template' : 'Create Template'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div>
            <label className="mb-1 block text-sm font-medium text-muted-foreground">
              Name <span className="text-destructive">*</span>
            </label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., ListNode with Cycle Helper"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block text-sm font-medium text-muted-foreground">
              Description
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Explains what this template provides..."
              className="min-h-[80px]"
            />
          </div>

          {/* Code Editor */}
          <div>
            <label className="mb-2 block text-sm font-medium text-muted-foreground">
              Template Code <span className="text-destructive">*</span>
            </label>
            <MultiLanguageCodeEditor
              value={codeData}
              onChange={setCodeData}
              languages={languages}
              showValidation={isEditMode} // Only show validation in edit mode
              showHelperCode
              codeLabel="Definition Code"
              onValidate={isEditMode ? handleValidate : undefined}
              validating={validating}
              height="250px"
            />
            {!isEditMode && (
              <p className="mt-2 text-xs text-muted-foreground">
                Save the template first, then you can add validation tests.
              </p>
            )}
          </div>

          {/* Validation Error */}
          {validationError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {validationError}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between border-t border-border pt-4">
            <div className="text-xs text-muted-foreground">
              {isEditMode
                ? 'Validation is recommended before using this template'
                : 'Save to enable validation testing'}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || validating}>
                {isSubmitting
                  ? 'Saving...'
                  : isEditMode
                    ? 'Update Template'
                    : 'Create Template'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
